/**
 * Страница массовой привязки пациентов по телефону
 * Дата: 29.12.2025, обновлено 29.01.2026
 * Назначение: UI для массовой привязки Telegram через поиск по номеру телефона
 * Дата: 26.01.2026 - Добавлено frontend логирование (TODO-034)
 * Дата: 29.01.2026 - TODO-021a: Добавлены фильтры, CheckboxTable, DateRangePicker, ProgressBar
 * Связан с: TODO-021 (mass_phone_binding), TODO-021a (enhancements)
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/utils/logger';
import { MainLayout } from '@/components/layout/MainLayout';
import { LinearScreen } from '@/features/linear-canvas/LinearScreen';
import { linearCanvas } from '@/features/linear-canvas/linearCanvas';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent, 
  Button,
  CheckboxTable,
  DateRangePicker,
  ProgressBar,
  TenantSelector,
  SearchInput
} from '@/components/ui';
import { useToast } from '@/hooks/useToast';
import { useTenantScope } from '@/hooks/useTenantScope';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { 
  Phone, 
  Search, 
  Users, 
  CheckCircle, 
  XCircle, 
  Clock,
  MessageSquare, 
  RefreshCw,
  Play,
  AlertCircle,
  TrendingUp
} from 'lucide-react';

// Типы для API
interface SearchStats {
  total_patients: number;
  bound_by_link: number;
  found_by_phone_search: number;
  not_found: number;
  pending: number;
}

interface BatchSearchJob {
  id: string;
  tenant_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  total_phones: number;
  processed: number;
  found: number;
  not_found: number;
  errors: number;
  started_at: string;
  completed_at?: string;
}

interface SyncResult {
  tenant_id: string;
  tenant_slug: string;
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  duration: string;
  synced_at: string;
}

// Тип для пациента в таблице (29.01.2026 — TODO-021a)
interface PatientRow {
  id: string;
  name?: string;
  phone: string;
  has_telegram: boolean;
  has_max: boolean;
  created_at: string;
  telegram_id?: string;
  telegram_username?: string;
  max_id?: string;
}

// API функции — Дата: 30.01.2026 — Исправлено: используем apiClient вместо fetch+localStorage
import { apiClient } from '@/api/client';

// 30.01.2026 - FIX: используем apiClient который автоматически добавляет токен
async function fetchStats(tenantId?: string): Promise<SearchStats> {
  const params: Record<string, string> = {};
  if (tenantId) params.tenant_id = tenantId;
  const res = await apiClient.get('/phone-binding/stats', { params });
  return res.data;
}

// 30.01.2026 - FIX: используем apiClient
async function startBatchSearch(options: { source: string; skip_already_found?: boolean; phones?: string[]; tenant_id?: string }): Promise<BatchSearchJob> {
  const res = await apiClient.post('/phone-binding/batch/search', options);
  return res.data;
}

// 30.01.2026 - FIX: используем apiClient
async function getJobStatus(jobId: string): Promise<BatchSearchJob> {
  const res = await apiClient.get(`/phone-binding/batch/jobs/${jobId}`);
  return res.data;
}

// 30.01.2026 - FIX: используем apiClient
async function syncPatientsToday(tenantId?: string): Promise<SyncResult> {
  const params: Record<string, string> = {};
  if (tenantId) params.tenant_id = tenantId;
  const res = await apiClient.post('/sync/patients/today', null, { params });
  return res.data.result;
}

// 30.01.2026 - FIX: используем apiClient
async function searchSinglePhone(phone: string, tenantId?: string): Promise<{ status: string; telegram_user_id?: number; telegram_username?: string; error_message?: string; first_name?: string }> {
  const res = await apiClient.post('/phone-binding/single/search', { phone, tenant_id: tenantId });
  return res.data;
}

// 30.01.2026 — TODO-021a: API для получения списка пациентов с фильтрацией (исправлено: используем apiClient)
async function fetchPatientsWithBindings(filters: {
  date_from?: string;
  date_to?: string;
  only_without_tg?: boolean;
  only_without_max?: boolean;
  phone_search?: string;
  limit?: number;
  offset?: number;
  tenant_id?: string;
}): Promise<PatientRow[]> {
  const params: Record<string, string | number | boolean> = {};
  if (filters.tenant_id) params.tenant_id = filters.tenant_id;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  if (filters.only_without_tg) params.only_without_tg = true;
  if (filters.only_without_max) params.only_without_max = true;
  if (filters.phone_search) params.phone_search = filters.phone_search;
  if (filters.limit) params.limit = filters.limit;
  if (filters.offset) params.offset = filters.offset;

  const res = await apiClient.get('/phone-binding/patients', { params });
  return res.data;
}

export function PhoneBindingPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<SearchStats | null>(null);
  const [currentJob, setCurrentJob] = useState<BatchSearchJob | null>(null);
  const [singlePhone, setSinglePhone] = useState('');
  const [patientPhoneSearch, setPatientPhoneSearch] = useState(''); // 12.02.2026 — поиск по телефону в списке пациентов
  const [singleSearchResult, setSingleSearchResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // 29.01.2026 — TODO-021a: Состояния для фильтров и выбора пациентов
  const [selectedPatientIds, setSelectedPatientIds] = useState<Set<string>>(new Set());
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [filterOnlyWithoutTG, setFilterOnlyWithoutTG] = useState(false);
  const [filterOnlyWithoutMAX, setFilterOnlyWithoutMAX] = useState(false);
  const [patients, setPatients] = useState<PatientRow[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  
  // 05.02.2026 — Пагинация
  const [pageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(true);
  
  const { showSuccess, showError, showWarning } = useToast();
  
  // 29.01.2026 - SuperAdmin tenant selection
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant, requireTenantSelection } = useTenantScope();

  // Логирование просмотра страницы
  useEffect(() => {
    logger.info('page_view', { page: 'phone-binding' });
  }, []);

  // Загрузить статистику при монтировании или смене тенанта
  useEffect(() => {
    if (tenantId) {
      loadStats();
    }
  }, [tenantId]);

  // 12.02.2026 — patientPhoneSearch обновляется из SearchInput (встроенный debounce)

  // 29.01.2026 — TODO-021a: Загрузить список пациентов при изменении фильтров или тенанта
  // 05.02.2026 — Добавлена пагинация
  // 12.02.2026 — Добавлена фильтрация по номеру телефона (debouncedPhone)
  useEffect(() => {
    if (tenantId) {
      loadPatients();
    }
  }, [tenantId, dateFrom, dateTo, filterOnlyWithoutTG, filterOnlyWithoutMAX, currentPage, patientPhoneSearch]);
  
  // 05.02.2026 — Сброс пагинации при изменении фильтров
  useEffect(() => {
    setCurrentPage(0);
  }, [dateFrom, dateTo, filterOnlyWithoutTG, filterOnlyWithoutMAX, tenantId, patientPhoneSearch]);

  // Логирование загрузки статистики
  useEffect(() => {
    if (stats) {
      logger.info('phone_binding_stats_loaded', {
        total_patients: stats.total_patients,
        bound_by_link: stats.bound_by_link,
        found_by_phone_search: stats.found_by_phone_search,
        not_found: stats.not_found,
        pending: stats.pending
      });
    }
  }, [stats]);

  // Polling для обновления статуса job
  useEffect(() => {
    if (!currentJob || currentJob.status === 'completed' || currentJob.status === 'failed') {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const job = await getJobStatus(currentJob.id);
        setCurrentJob(job);
        if (job.status === 'completed' || job.status === 'failed') {
          logger.info('phone_binding_batch_search_completed', {
            job_id: job.id,
            status: job.status,
            processed: job.processed,
            found: job.found,
            not_found: job.not_found,
            errors: job.errors
          });
          loadStats(); // Обновить статистику
          loadPatients(); // 12.02.2026 — Обновить список пациентов (покажет TG привязки)
        }
      } catch (err) {
        logger.error('phone_binding_batch_search_poll_failed', {
          job_id: currentJob.id,
          error: err instanceof Error ? err.message : 'Unknown error'
        });
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [currentJob]);

  const loadStats = async () => {
    try {
      const data = await fetchStats(tenantId || undefined);
      setStats(data);
    } catch (err) {
      logger.error('phone_binding_stats_load_failed', { 
        error: err instanceof Error ? err.message : 'Unknown error' 
      });
    }
  };

  // 29.01.2026 — TODO-021a: Загрузка списка пациентов с фильтрацией
  // 05.02.2026 — Добавлена пагинация
  const loadPatients = async () => {
    setLoadingPatients(true);
    try {
      const filters: any = {
        limit: pageSize + 1, // +1 чтобы определить есть ли следующая страница
        offset: currentPage * pageSize,
        tenant_id: tenantId || undefined
      };
      
      if (dateFrom) {
        filters.date_from = format(dateFrom, 'yyyy-MM-dd');
      }
      if (dateTo) {
        filters.date_to = format(dateTo, 'yyyy-MM-dd');
      }
      if (filterOnlyWithoutTG) {
        filters.only_without_tg = true;
      }
      if (filterOnlyWithoutMAX) {
        filters.only_without_max = true;
      }
      // 12.02.2026 — Partial search по номеру телефона (из SearchInput в секции списка)
      if (patientPhoneSearch.trim().length >= 3) {
        filters.phone_search = patientPhoneSearch.trim();
      }

      const data = await fetchPatientsWithBindings(filters);
      // 30.01.2026: Защита от null — гарантируем массив
      const result = data || [];
      
      // 05.02.2026: Определяем есть ли следующая страница
      setHasMorePages(result.length > pageSize);
      // Отрезаем лишнюю запись (мы запросили +1)
      setPatients(result.slice(0, pageSize));
      
      logger.info('phone_binding_patients_loaded', {
        count: Math.min(result.length, pageSize),
        page: currentPage,
        has_more: result.length > pageSize,
        filters: {
          date_from: filters.date_from,
          date_to: filters.date_to,
          only_without_tg: filters.only_without_tg,
          only_without_max: filters.only_without_max
        }
      });
    } catch (err) {
      logger.error('phone_binding_patients_load_failed', {
        error: err instanceof Error ? err.message : 'Unknown error'
      });
      showError('Ошибка загрузки списка пациентов');
    } finally {
      setLoadingPatients(false);
    }
  };

  const handleStartBatchSearch = async () => {
    logger.info('phone_binding_batch_search_start_click', {
      source: 'datalayer',
      skip_already_found: true
    });
    setLoading(true);
    try {
      const job = await startBatchSearch({
        source: 'datalayer',
        skip_already_found: true,
        tenant_id: tenantId || undefined
      });
      logger.info('phone_binding_batch_search_started', {
        job_id: job.id,
        total_phones: job.total_phones
      });
      setCurrentJob(job);
      showSuccess('Массовый поиск запущен');
    } catch (err: any) {
      logger.error('phone_binding_batch_search_start_failed', {
        error: err.message || 'Unknown error'
      });
      showError(err.message || 'Ошибка запуска поиска');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncPatients = async () => {
    logger.info('phone_binding_sync_patients_click');
    setSyncing(true);
    try {
      const result = await syncPatientsToday(tenantId || undefined);
      logger.info('phone_binding_sync_patients_success', {
        created: result.created,
        updated: result.updated,
        skipped: result.skipped,
        errors: result.errors,
        duration: result.duration
      });
      showSuccess(`Синхронизировано: ${result.created} новых, ${result.updated} обновлено`);
      loadStats();
      loadPatients();
    } catch (err: any) {
      logger.error('phone_binding_sync_patients_failed', {
        error: err.message || 'Unknown error'
      });
      showError(err.message || 'Ошибка синхронизации');
    } finally {
      setSyncing(false);
    }
  };

  const handleSingleSearch = async () => {
    if (!singlePhone.trim()) {
      showWarning('Введите номер телефона');
      return;
    }

    logger.info('phone_binding_single_search_click', {
      phone_length: singlePhone.length
    });
    setLoading(true);
    setSingleSearchResult(null);
    try {
      const result = await searchSinglePhone(singlePhone, tenantId || undefined);
      logger.info('phone_binding_single_search_result', {
        status: result.status,
        found: result.status === 'found',
        has_username: !!result.telegram_username
      });
      setSingleSearchResult(result);
      if (result.status === 'found') {
        showSuccess(`Найден: @${result.telegram_username || result.telegram_user_id}`);
      } else if (result.status === 'tg_client_unavailable') {
        showError(result.error_message || 'Telegram Client недоступен. Требуется повторная авторизация.');
      } else {
        showWarning('Пользователь не найден в Telegram');
      }
    } catch (err: any) {
      logger.error('phone_binding_single_search_failed', {
        error: err.message || 'Unknown error'
      });
      showError(err.message || 'Ошибка поиска');
    } finally {
      setLoading(false);
    }
  };


  // 29.01.2026 — TODO-021a: Обработчики для фильтров и массового поиска
  const handleStartSelectedSearch = async () => {
    if (selectedPatientIds.size === 0) {
      showWarning('Выберите хотя бы одного пациента');
      return;
    }

    logger.info('phone_binding_selected_search_start', {
      selected_count: selectedPatientIds.size,
      filter_only_without_tg: filterOnlyWithoutTG,
      filter_only_without_max: filterOnlyWithoutMAX
    });

    setLoading(true);
    try {
      // 29.01.2026 — TODO-021a: Используем API для поиска выбранных пациентов
      const selectedPhones = filteredPatients
        .filter(p => selectedPatientIds.has(p.id))
        .map(p => p.phone);

      const job = await startBatchSearch({
        source: 'manual',
        phones: selectedPhones,
        skip_already_found: false,
        tenant_id: tenantId || undefined
      });

      logger.info('phone_binding_selected_search_started', {
        job_id: job.id,
        phones_count: selectedPhones.length
      });

      setCurrentJob(job);
      setSelectedPatientIds(new Set()); // Сбросить выбор
      showSuccess(`Поиск запущен для ${selectedPhones.length} пациентов`);
    } catch (err: any) {
      logger.error('phone_binding_selected_search_failed', {
        error: err.message || 'Unknown error'
      });
      showError(err.message || 'Ошибка запуска поиска');
    } finally {
      setLoading(false);
    }
  };

  // Фильтрация уже выполняется на backend, но можно добавить дополнительную фильтрацию на frontend если нужно
  const filteredPatients = patients;

  return (
    <MainLayout headerTitle="Привязка по телефону" disableMainScroll>
      <LinearScreen
        eyebrow="Привязка"
        title="Поиск по телефону"
        lead="Синхронизация пациентов и поиск Telegram по номеру. Нужен подключённый Telegram Client."
        actions={
          <Button onClick={handleSyncPatients} disabled={syncing || requireTenantSelection}>
            <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Синхронизация...' : 'Синхронизировать'}
          </Button>
        }
        toolbar={
          isSuperAdmin ? (
            <div className="max-w-xs">
              <TenantSelector
                value={selectedTenantId || ''}
                onChange={setSelectedTenant}
                testId="select-tenant-phone-binding"
              />
            </div>
          ) : undefined
        }
      >
      <div className="space-y-4">
        {requireTenantSelection && (
          <div className={linearCanvas.notice}>Выберите клинику для просмотра данных.</div>
        )}
        
        {/* Статистика */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="border-shell-border bg-shell-surface shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Всего пациентов</p>
                  <p className="text-2xl font-bold">{stats?.total_patients ?? '—'}</p>
                </div>
                <Users className="w-8 h-8 opacity-60" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-shell-border bg-shell-surface shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Привязаны (ссылка)</p>
                  <p className="text-2xl font-bold">{stats?.bound_by_link ?? '—'}</p>
                </div>
                <CheckCircle className="w-8 h-8 opacity-60" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-shell-border bg-shell-surface shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Найдены поиском</p>
                  <p className="text-2xl font-bold">{stats?.found_by_phone_search ?? '—'}</p>
                </div>
                <Search className="w-8 h-8 opacity-60" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-shell-border bg-shell-surface shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Не найдены</p>
                  <p className="text-2xl font-bold">{stats?.not_found ?? '—'}</p>
                </div>
                <XCircle className="w-8 h-8 opacity-60" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-shell-border bg-shell-surface shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Ожидают</p>
                  <p className="text-2xl font-bold">{stats?.pending ?? '—'}</p>
                </div>
                <Clock className="w-8 h-8 opacity-60" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Синхронизация из DataLayer */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Синхронизация пациентов
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600">
                  Загрузить новых пациентов из базы клиники за сегодня
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  Источник: Data Layer API
                </p>
              </div>
              <Button 
                onClick={handleSyncPatients} 
                disabled={syncing}
                className="flex items-center gap-2"
              >
                {syncing ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Синхронизация...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Синхронизировать
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Массовый поиск */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Массовый поиск в Telegram
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600">
                  Запустить поиск всех непривязанных пациентов в Telegram по номеру телефона
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  ⚠️ Требуется подключенный Telegram Client (MTProto)
                </p>
              </div>
              <Button 
                onClick={handleStartBatchSearch} 
                disabled={loading || (currentJob?.status === 'running')}
                className="flex items-center gap-2"
              >
                {currentJob?.status === 'running' ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Выполняется...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Запустить поиск
                  </>
                )}
              </Button>
            </div>

            {/* Прогресс текущей задачи */}
            {currentJob && (
              <div className="mt-4 p-4 bg-neutral-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">
                    {currentJob.status === 'running' ? 'Выполняется поиск...' : 
                     currentJob.status === 'completed' ? 'Поиск завершён' : 
                     currentJob.status === 'failed' ? 'Ошибка' : 'Ожидание'}
                  </span>
                  <span className="text-sm text-neutral-500">
                    {currentJob.processed} / {currentJob.total_phones}
                  </span>
                </div>
                
                {/* 29.01.2026 — TODO-021a: Используем компонент ProgressBar */}
                <ProgressBar
                  value={currentJob.processed}
                  max={currentJob.total_phones}
                  variant={currentJob.status === 'completed' ? 'success' : currentJob.status === 'failed' ? 'error' : 'default'}
                  showPercent={true}
                  className="mb-3"
                />

                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-lg font-bold text-green-600">{currentJob.found}</p>
                    <p className="text-xs text-neutral-500">Найдено</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-orange-600">{currentJob.not_found}</p>
                    <p className="text-xs text-neutral-500">Не найдено</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-red-600">{currentJob.errors}</p>
                    <p className="text-xs text-neutral-500">Ошибок</p>
                  </div>
                </div>

                {/* 12.02.2026 — Показать найденные привязки после завершения поиска */}
                {currentJob.status === 'completed' && currentJob.found > 0 && (
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-medium text-green-800">
                      Привязка найдена!
                    </p>
                    {patients.filter(p => p.has_telegram && p.telegram_id).map(p => (
                      <p key={p.id} className="text-sm text-green-700 mt-1">
                        {p.name || p.phone}: TG ID {p.telegram_id}
                        {p.telegram_username ? ` (@${p.telegram_username})` : ''}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Поиск одного номера */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Поиск по номеру
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <input
                type="tel"
                value={singlePhone}
                onChange={(e) => setSinglePhone(e.target.value)}
                placeholder="+7 999 123-45-67"
                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              />
              <Button 
                onClick={handleSingleSearch}
                disabled={loading}
                className="flex items-center gap-2"
              >
                <Search className="w-4 h-4" />
                Найти
              </Button>
            </div>

            {/* Результат поиска */}
            {singleSearchResult && (
              <div className={`mt-4 p-4 rounded-lg ${
                singleSearchResult.status === 'found' 
                  ? 'bg-green-50 border border-green-200' 
                  : singleSearchResult.status === 'tg_client_unavailable'
                    ? 'bg-red-50 border border-red-200'
                    : 'bg-orange-50 border border-orange-200'
              }`}>
                {singleSearchResult.status === 'found' ? (
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Найден в Telegram</p>
                      <p className="text-sm text-green-600">
                        {singleSearchResult.telegram_username 
                          ? `@${singleSearchResult.telegram_username}` 
                          : `ID: ${singleSearchResult.telegram_user_id}`
                        }
                      </p>
                    </div>
                  </div>
                ) : singleSearchResult.status === 'tg_client_unavailable' ? (
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="font-medium text-red-800">Telegram Client недоступен</p>
                      <p className="text-sm text-red-600">
                        {singleSearchResult.error_message || 'Сессия истекла. Перейдите в раздел Telegram Client для повторной авторизации.'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-orange-600" />
                    <div>
                      <p className="font-medium text-orange-800">Не найден</p>
                      <p className="text-sm text-orange-600">
                        Пользователь не зарегистрирован в Telegram или скрыл номер
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* 29.01.2026 — TODO-021a: Список пациентов с фильтрами */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Список пациентов
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Фильтры */}
            <div className="space-y-4">
              {/* 12.02.2026 — Поиск по номеру телефона в списке пациентов */}
              <SearchInput
                value={patientPhoneSearch}
                onChange={setPatientPhoneSearch}
                placeholder="Поиск по номеру телефона (мин. 3 цифры)..."
                debounceMs={400}
                size="md"
                showClear
                testId="patient-phone-search"
              />

              <DateRangePicker
                from={dateFrom}
                to={dateTo}
                onChange={(from, to) => {
                  setDateFrom(from);
                  setDateTo(to);
                }}
              />

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filterOnlyWithoutTG}
                    onChange={(e) => setFilterOnlyWithoutTG(e.target.checked)}
                    className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-600">Только без Telegram</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filterOnlyWithoutMAX}
                    onChange={(e) => setFilterOnlyWithoutMAX(e.target.checked)}
                    className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-600">Только без MAX</span>
                </label>
              </div>
            </div>

            {/* Кнопка запуска поиска выбранных */}
            <div className="flex items-center justify-between">
              <div className="text-sm text-neutral-600">
                Выбрано: {selectedPatientIds.size} из {filteredPatients.length}
              </div>
              <Button
                onClick={handleStartSelectedSearch}
                disabled={loading || selectedPatientIds.size === 0}
                className="flex items-center gap-2"
              >
                <Play className="w-4 h-4" />
                Запустить поиск выбранных ({selectedPatientIds.size})
              </Button>
            </div>

            {/* Таблица пациентов */}
            {loadingPatients ? (
              <div className="text-center py-8 text-neutral-500">
                Загрузка пациентов...
              </div>
            ) : filteredPatients.length > 0 ? (
              <>
                <CheckboxTable<PatientRow>
                  data={filteredPatients}
                  columns={[
                    {
                      key: 'name',
                      header: 'ФИО',
                      render: (row) => <span className="font-medium">{row.name || '—'}</span>
                    },
                    {
                      key: 'phone',
                      header: 'Телефон',
                      render: (row) => <span className="text-neutral-600">{row.phone}</span>
                    },
                    {
                      key: 'telegram',
                      header: 'Telegram',
                      render: (row) => (
                        row.has_telegram ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-neutral-400" />
                        )
                      )
                    },
                    {
                      key: 'max',
                      header: 'MAX',
                      render: (row) => (
                        row.has_max ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-neutral-400" />
                        )
                      )
                    },
                    {
                      key: 'created',
                      header: 'Создан',
                      render: (row) => (
                        <span className="text-sm text-neutral-500">
                          {format(new Date(row.created_at), 'dd.MM.yyyy', { locale: ru })}
                        </span>
                      )
                    },
                    {
                      key: 'actions',
                      header: 'Действия',
                      render: (row) => (
                        (row.has_telegram || row.has_max) ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              navigate(`/messages?phone=${encodeURIComponent(row.phone)}&initiate=true`);
                            }}
                            className="flex items-center gap-1"
                            testId={`btn-write-${row.id}`}
                          >
                            <MessageSquare className="w-4 h-4" />
                            <span>Написать</span>
                          </Button>
                        ) : (
                          <span className="text-sm text-neutral-400">—</span>
                        )
                      )
                    }
                  ]}
                  selectedIds={selectedPatientIds}
                  onSelectionChange={setSelectedPatientIds}
                  getRowId={(row) => row.id}
                  isRowDisabled={(row) => row.has_telegram && row.has_max} // Нельзя выбрать уже привязанных
                />

                {/* 05.02.2026 — Пагинация */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-neutral-600">
                    Страница {currentPage + 1} · Показано {filteredPatients.length} записей
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                      disabled={currentPage === 0 || loadingPatients}
                    >
                      ← Назад
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={!hasMorePages || loadingPatients}
                    >
                      Вперёд →
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-neutral-500">
                {patients.length === 0 
                  ? 'Пациенты не загружены. Запустите синхронизацию.'
                  : 'Нет пациентов, соответствующих фильтрам'}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Информация */}
        <div className={linearCanvas.notice}>
            <div className="flex gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <p className="mb-1 font-medium">Как работает поиск по номеру</p>
                <ul className="list-inside list-disc space-y-1">
                  <li>Поиск работает только через Telegram Client API (MTProto)</li>
                  <li>Бот API не поддерживает поиск по номеру телефона</li>
                  <li>Пользователь будет найден только если его номер виден в Telegram</li>
                  <li>Приватность настраивается в настройках Telegram пациента</li>
                  <li>Повторный поиск ненайденных номеров — через 30 дней</li>
                </ul>
              </div>
            </div>
        </div>
      </div>
      </LinearScreen>
    </MainLayout>
  );
}

export default PhoneBindingPage;

